/**
 * Health Check API Client for TECDocChart Multi-Tenant Architecture
 * 
 * Handles communication with Django tenant-aware health check endpoints.
 * Automatically uses tenant context from domain-based routing.
 * 
 * HIPAA Compliance:
 * - No PHI data in health checks
 * - Session-based authentication
 * - Audit logging via Django backend
 */

import axios from 'axios';
import type { AxiosResponse } from 'axios';
import type { HealthCheckResponse } from '../types/health.types';
import { getApiBaseUrl, getTenantConfig } from '../config/tenant';

// Create axios instance with tenant-aware configuration
const createApiClient = () => {
  try {
    // Get tenant-specific API configuration
    const baseURL = getApiBaseUrl();
    
    return axios.create({
      baseURL,
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      withCredentials: true, // Include Django session cookies for tenant auth
    });
  } catch {
    // Fallback configuration if tenant config not available
    return axios.create({
      baseURL: '/api/',
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      withCredentials: true,
    });
  }
};

export class HealthApiClient {
  /**
   * Get tenant-aware health check data from Django backend
   */
  static async getHealthCheck(): Promise<HealthCheckResponse> {
    try {
      const api = createApiClient();
      
      // Add request interceptor for tenant-aware logging
      api.interceptors.request.use((config) => {
        try {
          const tenant = getTenantConfig();
          console.log(`[${tenant.tenant.schema}] Health API Request:`, config.method?.toUpperCase(), config.url);
        } catch {
          console.log('Health API Request:', config.method?.toUpperCase(), config.url);
        }
        return config;
      });

      // Response interceptor for tenant-aware error handling
      api.interceptors.response.use(
        (response) => response,
        (error) => {
          try {
            const tenant = getTenantConfig();
            console.error(`[${tenant.tenant.schema}] Health API Error:`, error.response?.data || error.message);
          } catch {
            console.error('Health API Error:', error.response?.data || error.message);
          }
          return Promise.reject(error);
        }
      );

      const response: AxiosResponse<HealthCheckResponse> = await api.get('/health/');
      
      // Validate response structure
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid health check response format');
      }

      // Log successful health check for audit purposes
      try {
        const tenant = getTenantConfig();
        console.log(`[${tenant.tenant.schema}] Health check successful:`, response.data.status);
      } catch {
        console.log('Health check successful:', response.data.status);
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.detail || error.message;
        throw new Error(`Health check failed: ${errorMessage}`);
      }
      throw error;
    }
  }

  /**
   * Test API connectivity with tenant context
   */
  static async pingApi(): Promise<boolean> {
    try {
      const api = createApiClient();
      await api.get('/health/', { timeout: 2000 });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get comprehensive system status (for debugging)
   */
  static async getSystemStatus(): Promise<any> {
    try {
      const api = createApiClient();
      const response = await api.get('/status/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.detail || error.message;
        throw new Error(`System status failed: ${errorMessage}`);
      }
      throw error;
    }
  }
}

export default HealthApiClient;